const React = require('react');
const { createElement: h } = React;
const VariableAutocompleteInput = require('./VariableAutocompleteInput');
const {
  OPERATOR_OPTIONS,
  operatorRequiresValue,
  nextConditionId,
  nextGroupId,
  normalizeConditionItem,
  normalizeConditionRule
} = require('../lib/conditionRule');

// Sidebar editor for the Condition node. Mirrors the previous popup
// (ConditionRuleEditor.js) so users get full Simple/Advanced parity here
// instead of a separate balloon. Layout:
//
//   Variable                      ← chip rendered by parent (renderVariableChip)
//   [ Simple | Advanced ] toggle
//   Conditions                    ← single row (simple) OR grouped tree (advanced)
//
// Editing emits a fresh `conditionRule` via onConditionRuleChange after every
// keystroke; the parent persists it. The mode lives on the rule itself
// (`rule.mode`), so reopening the node restores whichever tab the user left.

function createEmptyCondition() {
  return { type: 'condition', id: nextConditionId(), operator: 'equals', value: '' };
}

// Read the rule, return a sanitised root group + mode. The on-disk shape may
// be a flat list or a nested group; normalizeConditionRule already handles
// both, but we still need to ensure root.items is non-empty so the editor has
// something to render.
function ensureRoot(rule) {
  var normalized = normalizeConditionRule(rule);
  var root = normalized.root || { type: 'group', id: nextGroupId(), logic: 'and', items: [createEmptyCondition()] };
  if (!Array.isArray(root.items) || !root.items.length) {
    root = Object.assign({}, root, { items: [createEmptyCondition()] });
  }
  return {
    mode: normalized.mode === 'advanced' ? 'advanced' : 'simple',
    root: root
  };
}

function firstConditionFromItems(items) {
  for (var i = 0; i < items.length; i += 1) {
    var item = items[i];
    if (item && item.type === 'condition') return item;
    if (item && item.type === 'group' && Array.isArray(item.items)) {
      var nested = firstConditionFromItems(item.items);
      if (nested) return nested;
    }
  }
  return null;
}

// Build the on-disk rule shape from the editor's working state. Keeps both
// the legacy flat (`conditions: [...]`) and modern nested (`root: {...}`)
// fields populated so older readers stay compatible.
function buildRule(mode, root) {
  var safeMode = mode === 'advanced' ? 'advanced' : 'simple';
  var rootItems = Array.isArray(root.items) ? root.items : [];
  var trimmedRoot;

  if (safeMode === 'simple') {
    var first = firstConditionFromItems(rootItems) || createEmptyCondition();
    trimmedRoot = {
      type: 'group',
      id: root.id || nextGroupId(),
      logic: 'and',
      items: [normalizeConditionItem(first)]
    };
  } else {
    trimmedRoot = {
      type: 'group',
      id: root.id || nextGroupId(),
      logic: String(root.logic || '').toLowerCase() === 'or' ? 'or' : 'and',
      items: rootItems
    };
  }

  // Flat conditions for the legacy `conditions:` field — only the first level
  // of conditions is included (groups don't flatten cleanly).
  var flatConditions = (trimmedRoot.items || [])
    .filter(function(it) { return it && it.type === 'condition'; })
    .map(normalizeConditionItem);

  return {
    mode: safeMode,
    logic: trimmedRoot.logic,
    conditions: flatConditions,
    root: trimmedRoot
  };
}

// Single condition row: operator dropdown + value input (with @-variable
// autocomplete). Used in both Simple and Advanced modes.
function ConditionRow(props) {
  var condition = props.condition;
  var onUpdate = props.onUpdate;
  var onRemove = props.onRemove;
  var removable = props.removable;
  var variableNodes = props.variableNodes || [];
  var needsValue = operatorRequiresValue(condition.operator);

  return h('div', { className: 'wf-cond-row' },
    h('select', {
      className: 'wf-params-input wf-cond-operator',
      value: condition.operator,
      onChange: function(e) {
        // updateRootCondition / updateGroupCondition already clear `value`
        // when the new operator doesn't need one. Calling onUpdate('value', '')
        // here would re-emit using a stale rootItems closure and clobber the
        // operator change, snapping the dropdown back to "=".
        onUpdate('operator', e.target.value);
      }
    },
      OPERATOR_OPTIONS.map(function(opt) {
        return h('option', { key: opt.value, value: opt.value }, opt.label);
      })
    ),
    h(VariableAutocompleteInput, {
      className: 'wf-cond-value',
      value: condition.value,
      placeholder: needsValue ? 'Value (type @ for variables)' : 'No value needed',
      disabled: !needsValue,
      variableNodes: variableNodes,
      onChange: function(next) { onUpdate('value', next); }
    }),
    removable
      ? h('button', {
          type: 'button',
          className: 'wf-cond-remove',
          onClick: onRemove,
          title: 'Remove condition'
        }, 'x')
      : null
  );
}

function ConditionSidebarEditor(props) {
  var conditionRule = props.conditionRule || null;
  var variableNodes = props.variableNodes || [];
  var onConditionRuleChange = props.onConditionRuleChange || function() {};
  var renderVariableChip = props.renderVariableChip;

  var state = ensureRoot(conditionRule);
  var mode = state.mode;
  var root = state.root;
  var rootItems = Array.isArray(root.items) ? root.items : [];

  // Every mutation funnels through emit() so we always persist a fully-built
  // rule and never store inconsistent intermediate state on the node.
  function emit(nextMode, nextRoot) {
    onConditionRuleChange(buildRule(nextMode, nextRoot));
  }

  function setMode(nextMode) {
    var resolved = nextMode === 'advanced' ? 'advanced' : 'simple';
    if (resolved === mode) return;
    if (resolved === 'simple') {
      // Collapse back to a single row using the first condition we can find,
      // discarding groups so the simple view has a clean starting point.
      var first = firstConditionFromItems(rootItems) || createEmptyCondition();
      emit('simple', Object.assign({}, root, { logic: 'and', items: [normalizeConditionItem(first)] }));
    } else {
      emit('advanced', root);
    }
  }

  function setRootLogic(value) {
    emit(mode, Object.assign({}, root, { logic: value === 'or' ? 'or' : 'and' }));
  }

  function updateRootCondition(conditionId, field, value) {
    var nextItems = rootItems.map(function(item) {
      if (!item || item.type !== 'condition' || item.id !== conditionId) return item;
      var next = Object.assign({}, item, { [field]: value });
      if (field === 'operator' && !operatorRequiresValue(value)) next.value = '';
      return next;
    });
    emit(mode, Object.assign({}, root, { items: nextItems }));
  }

  function removeRootCondition(conditionId) {
    var nextItems = rootItems.filter(function(it) {
      return !(it && it.type === 'condition' && it.id === conditionId);
    });
    if (!nextItems.length) nextItems = [createEmptyCondition()];
    emit(mode, Object.assign({}, root, { items: nextItems }));
  }

  function addRootCondition() {
    emit(mode, Object.assign({}, root, { items: rootItems.concat(createEmptyCondition()) }));
  }

  function addGroup() {
    emit(mode, Object.assign({}, root, {
      items: rootItems.concat({
        type: 'group',
        id: nextGroupId(),
        logic: 'and',
        items: [createEmptyCondition()]
      })
    }));
  }

  function removeGroup(groupId) {
    var nextItems = rootItems.filter(function(it) {
      return !(it && it.type === 'group' && it.id === groupId);
    });
    if (!nextItems.length) nextItems = [createEmptyCondition()];
    emit(mode, Object.assign({}, root, { items: nextItems }));
  }

  function updateGroupLogic(groupId, value) {
    var nextItems = rootItems.map(function(it) {
      if (!it || it.type !== 'group' || it.id !== groupId) return it;
      return Object.assign({}, it, { logic: value === 'or' ? 'or' : 'and' });
    });
    emit(mode, Object.assign({}, root, { items: nextItems }));
  }

  function addGroupCondition(groupId) {
    var nextItems = rootItems.map(function(it) {
      if (!it || it.type !== 'group' || it.id !== groupId) return it;
      return Object.assign({}, it, { items: (it.items || []).concat(createEmptyCondition()) });
    });
    emit(mode, Object.assign({}, root, { items: nextItems }));
  }

  function updateGroupCondition(groupId, conditionId, field, value) {
    var nextItems = rootItems.map(function(it) {
      if (!it || it.type !== 'group' || it.id !== groupId) return it;
      var nextInner = (it.items || []).map(function(cond) {
        if (!cond || cond.type !== 'condition' || cond.id !== conditionId) return cond;
        var next = Object.assign({}, cond, { [field]: value });
        if (field === 'operator' && !operatorRequiresValue(value)) next.value = '';
        return next;
      });
      return Object.assign({}, it, { items: nextInner });
    });
    emit(mode, Object.assign({}, root, { items: nextItems }));
  }

  function removeGroupCondition(groupId, conditionId) {
    var nextItems = rootItems.map(function(it) {
      if (!it || it.type !== 'group' || it.id !== groupId) return it;
      var nextInner = (it.items || []).filter(function(cond) {
        return !(cond && cond.type === 'condition' && cond.id === conditionId);
      });
      if (!nextInner.length) nextInner = [createEmptyCondition()];
      return Object.assign({}, it, { items: nextInner });
    });
    emit(mode, Object.assign({}, root, { items: nextItems }));
  }

  // ----- render helpers -----

  function renderModeToggle() {
    return h('div', { className: 'wf-params-toggle' },
      h('button', {
        type: 'button',
        className: 'wf-params-toggle-btn' + (mode === 'simple' ? ' wf-params-toggle-btn-active' : ''),
        onClick: function() { setMode('simple'); }
      }, 'Simple'),
      h('button', {
        type: 'button',
        className: 'wf-params-toggle-btn' + (mode === 'advanced' ? ' wf-params-toggle-btn-active' : ''),
        onClick: function() { setMode('advanced'); }
      }, 'Advanced')
    );
  }

  function renderSimple() {
    var first = rootItems[0] && rootItems[0].type === 'condition'
      ? rootItems[0]
      : createEmptyCondition();
    return h('div', { className: 'wf-cond-rows' },
      h(ConditionRow, {
        condition: first,
        removable: false,
        variableNodes: variableNodes,
        onUpdate: function(field, value) {
          // If the rule has no condition row yet, mint one so updates land
          // somewhere the next render can pick up.
          if (!rootItems[0] || rootItems[0].type !== 'condition') {
            var seeded = Object.assign({}, createEmptyCondition(), { [field]: value });
            emit('simple', Object.assign({}, root, { items: [seeded] }));
            return;
          }
          updateRootCondition(rootItems[0].id, field, value);
        }
      })
    );
  }

  function renderJoiner(label, isSub) {
    return h('div', {
      className: 'wf-cond-joiner' + (isSub ? ' wf-cond-joiner-sub' : '')
    }, label);
  }

  function renderAdvancedItem(item, index) {
    if (item && item.type === 'group') {
      var groupItems = Array.isArray(item.items) ? item.items : [];
      return h('div', { key: item.id, className: 'wf-cond-group' },
        index > 0 ? renderJoiner(String(root.logic || 'and').toUpperCase(), false) : null,
        h('div', { className: 'wf-cond-group-head' },
          h('div', { className: 'wf-cond-group-title' }, 'Group'),
          h('div', { className: 'wf-cond-group-actions' },
            h('select', {
              className: 'wf-params-input wf-cond-group-logic',
              value: item.logic || 'and',
              onChange: function(e) { updateGroupLogic(item.id, e.target.value); }
            },
              h('option', { value: 'and' }, 'AND'),
              h('option', { value: 'or' }, 'OR')
            ),
            h('button', {
              type: 'button',
              className: 'wf-cond-group-remove',
              onClick: function() { removeGroup(item.id); }
            }, 'Remove')
          )
        ),
        h('div', { className: 'wf-cond-group-body' },
          groupItems.map(function(cond, condIndex) {
            return h('div', { key: cond.id, className: 'wf-cond-row-wrap' },
              condIndex > 0 ? renderJoiner(String(item.logic || 'and').toUpperCase(), true) : null,
              h(ConditionRow, {
                condition: cond,
                removable: groupItems.length > 1,
                variableNodes: variableNodes,
                onUpdate: function(field, value) { updateGroupCondition(item.id, cond.id, field, value); },
                onRemove: function() { removeGroupCondition(item.id, cond.id); }
              })
            );
          }),
          h('button', {
            type: 'button',
            className: 'wf-cond-add wf-cond-add-sub',
            onClick: function() { addGroupCondition(item.id); }
          }, '+ Add condition')
        )
      );
    }

    return h('div', { key: item.id, className: 'wf-cond-row-wrap' },
      index > 0 ? renderJoiner(String(root.logic || 'and').toUpperCase(), false) : null,
      h(ConditionRow, {
        condition: item,
        removable: rootItems.length > 1,
        variableNodes: variableNodes,
        onUpdate: function(field, value) { updateRootCondition(item.id, field, value); },
        onRemove: function() { removeRootCondition(item.id); }
      })
    );
  }

  function renderAdvanced() {
    return h('div', { className: 'wf-cond-rows' },
      h('div', { className: 'wf-params-row' },
        h('div', { className: 'wf-params-row-label' }, 'Root group logic'),
        h('select', {
          className: 'wf-params-input',
          value: root.logic || 'and',
          onChange: function(e) { setRootLogic(e.target.value); }
        },
          h('option', { value: 'and' }, 'AND'),
          h('option', { value: 'or' }, 'OR')
        )
      ),
      rootItems.map(function(item, index) { return renderAdvancedItem(item, index); }),
      h('div', { className: 'wf-cond-add-row' },
        h('button', {
          type: 'button',
          className: 'wf-cond-add',
          onClick: addRootCondition
        }, '+ Add condition'),
        h('button', {
          type: 'button',
          className: 'wf-cond-add',
          onClick: addGroup
        }, '+ Add group')
      )
    );
  }

  return h('div', { className: 'wf-params-form' },
    renderVariableChip
      ? h('div', { className: 'wf-params-row' },
          h('div', { className: 'wf-params-row-label' }, 'Variable'),
          h('div', { className: 'wf-params-row-control' }, renderVariableChip())
        )
      : null,
    h('div', { className: 'wf-params-row' },
      h('div', { className: 'wf-params-row-label' }, 'Mode'),
      renderModeToggle()
    ),
    h('div', { className: 'wf-params-row' },
      h('div', { className: 'wf-params-row-label' }, 'Conditions'),
      mode === 'advanced' ? renderAdvanced() : renderSimple()
    )
  );
}

module.exports = ConditionSidebarEditor;
